import { PlayArrow, ReplayCircleFilledOutlined } from "@mui/icons-material";
import { Box, IconButton } from "@mui/material";
import React, { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  resetLightMetrics,
  startLightMetricsLiveStreaming,
  stopLightMetricsLiveStreaming,
} from "./redux/slice";
import { LinearProgress } from "@gpstrackit/gtc-react-controls";
import Hls from "hls.js";
import { useIntl } from "react-intl";
import { NotificationManager } from "Components/Notifications";

const MAPPING_EVENTS_CODES = [
  "event.Vehicle-Ignition-Off",
  "event.TripEnded",
]
const FIVE_MINUTES_IN_MILLISECONDS = new Date(Date.now() - 5 * 60 * 1000);

export const VideoPlayer = ({ children, retryCallback }) => {

  const dispatch = useDispatch();

  const handleRetry = useCallback(() => {
    dispatch(resetLightMetrics());
  }, [dispatch]);

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="100%"
      height="100%"
      bgcolor="black"
      flexDirection="column"
      gap={2}
    >
      {children}

      {retryCallback && (
        <IconButton onClick={(e) => { e.stopPropagation(); handleRetry(); }} size="large" style={{ color: "white" }}>
          <ReplayCircleFilledOutlined style={{ color: "white", fontSize: 35 }} />
        </IconButton>
      )}

    </Box>
  );
};

export const Playback = ({ handlePlay, isLoading }) => {
  return (
    <VideoPlayer>
      {isLoading ? (
        <Box width="200px">
          <LinearProgress />
        </Box>
      ) : (
        <PlayArrow
          onClick={(e) => {
            e.stopPropagation();
            handlePlay();
          }}
          style={{ color: "white", fontSize: 50, cursor: "pointer" }}
        />
      )}
    </VideoPlayer>
  );
};

export const LiveMetricsVideo = ({ lastReading, deviceId }) => {
  const dispatch = useDispatch();
  const { messages } = useIntl();
  const videoRef = useRef(null);
  const { streamRequestId, streamSessionURL, message, isExistingSession, error, loading } =
    useSelector((state) => state.lightMetricsRedux);
  const eventCode = lastReading?.eventCode;
  const lastMessageTime = lastReading?.LastMessageTime;

  const isIgnitionOff = MAPPING_EVENTS_CODES.includes(eventCode)
  const isPassed5Minutes = lastMessageTime && new Date(lastMessageTime) < FIVE_MINUTES_IN_MILLISECONDS;

  const handlePlay = useCallback(() => {
    dispatch(startLightMetricsLiveStreaming({ deviceId }));
  }, [deviceId]);

  useEffect(() => {
    return () => {
      dispatch(resetLightMetrics());
      if (!streamRequestId) return;
      dispatch(stopLightMetricsLiveStreaming({ deviceId, streamRequestId }));
    };
  }, []);

  useEffect(() => {
    if (videoRef.current) {
      if (Hls.isSupported()) {
        const hls = new Hls();
        hls.loadSource(streamSessionURL);
        hls.attachMedia(videoRef.current);
      } else if (
        videoRef.current.canPlayType("application/vnd.apple.mpegurl")
      ) {
        // Safari soporta HLS nativamente
        videoRef.current.src = streamSessionURL;
      }
    }
  }, [streamSessionURL]);


  if (error) {
    return (
      <VideoPlayer retryCallback>
        <span style={{ color: "white", fontSize: 16, textAlign: "center" }}>
          {messages["lightMetricsVideo_liveNotAvailable"]}
        </span>
      </VideoPlayer>
    )
  }

  if (isIgnitionOff || isPassed5Minutes) {
    return (
      <VideoPlayer>
        <span style={{ color: "white", fontSize: 16, textAlign: "center" }}>
          {messages["lightMetricsVideo_liveNotAvailable_ignitionOff"]}
        </span>
      </VideoPlayer>
    )
  }

  return (
    <>
      {streamSessionURL && !loading && streamRequestId ? (
        <Box position="relative" width="100%" height="100%">
          {isExistingSession && (
            <Box
              position="absolute"
              top={8}
              left={8}
              right={8}
              zIndex={1}
              bgcolor="rgba(0, 0, 0, 0.6)"
              borderRadius={1}
              padding="4px 8px"
            >
              <span style={{ color: "white", fontSize: 13 }}>
                {messages["lightMetricsVideo_joiningExistingSession"]}
              </span>
            </Box>
          )}
          <video
            ref={videoRef}
            controls
            autoPlay
            style={{ width: "100%", height: "100%", backgroundColor: "black" }}
          />
        </Box>
      ) : (
        <Playback handlePlay={handlePlay} isLoading={loading} />
      )}
    </>
  );
};
